import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {FitmentMasterNode} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/flexibleFitmentEngineWorkspaceCapability/domain/FitmentMasterNode";
import type {TuffexProductCatalogueRow} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductCatalogueWorkspaceCapability/domain/TuffexProductCatalogueRow";
import {
    countFitmentMasterNodes,
    createFitmentMasterNode,
    deleteFitmentMasterNode,
    getFitmentMasterNodeById,
    listFitmentMasterNodeColumnValues,
    listFitmentMasterNodes,
    updateFitmentMasterNode,
    type FitmentMasterNodeListColumn,
    type FitmentMasterNodeListFilters,
} from "~/domainModels/ottokart/fitment";
import type {Variant} from "~/domainModels/ottokart/products";
import {
    buildLookupCatalogueAdditionalColumnValues,
    createCatalogueRow,
    deleteCatalogueRow,
    countCatalogueRowsForTemplate,
    fetchCatalogueRowsForTemplate,
    listCatalogueColumnValues,
    namedFiltersFromTextColumns,
    type CatalogueTextColumnFilters,
    findCatalogueRowBySlug,
    loadTuffexMachineTypeCatalogue,
    mapLookupCatalogueRowToDisplayFields,
    updateCatalogueRow,
    type TuffexLookupColumnMapping,
} from "~/domainModels/ottokart/tuffexProductCatalogue";
import type {PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import {OTTOKART_ADMIN_MACHINE_FITMENT_NODE_TYPE} from "~/utilities/ottokart/tuffexProductCatalogueConfiguration";
import {
    findExistingMediaLibraryFolderId,
    listMediaLibraryFolderFileDirents,
    MACHINE_TYPES_MEDIA_LIBRARY_FOLDER_NAME,
} from "~/utilities/ottokart/mediaLibraryFolders";
import type {ZodType} from "zod";
import {z as zod} from "zod";

const PLACEHOLDER_MACHINE_TYPE_ID = "00000000-0000-4000-8000-000000000001" as Uuid;

const MACHINE_FITMENT_ATTR_CREATED_BY = "createdBy";
const MACHINE_FITMENT_ATTR_IMAGE_ID = "imageId";
const MACHINE_FITMENT_ATTR_MACHINE_TYPE_ID = "machineTypeId";
const MACHINE_FITMENT_ATTR_MANUFACTURER_ID = "manufacturerId";
const MACHINE_FITMENT_ATTR_SLUG = "slug";
const MACHINE_FITMENT_ATTR_SORT_ORDER = "sortOrder";
const MACHINE_FITMENT_ATTR_UPDATED_BY = "updatedBy";

export type Machine = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    displayName: string;
    type: Uuid;
    manufacturerId: Uuid | null;
    imageId?: Uuid | null;
    slug: string;
    sortOrder: number;
};

export type PostMachine = {
    createdBy: Uuid;
    updatedBy?: Uuid;
    displayName: string;
    type: Uuid;
    manufacturerId: Uuid | null;
    imageId?: Uuid | null;
    slug: string;
    sortOrder: number;
};

export type ListMachinesRequest = {
    page?: number;
    size?: number;
    sort?: string;
    type?: Uuid;
    manufacturer?: Uuid;
};

export type MachineListServerFilterColumn = "displayName" | "type" | "manufacturerId" | "slug" | "createdAt";

export type MachineListServerFilters = {
    createdAtFrom?: string | null;
    createdAtTo?: string | null;
    displayNames?: Array<string>;
    machineTypeIds?: Array<string>;
    manufacturerIds?: Array<string>;
    slugs?: Array<string>;
};

function machineListFiltersToFitmentFilters(
    filters: MachineListServerFilters | undefined,
): FitmentMasterNodeListFilters | undefined {
    if (filters === undefined) {
        return undefined;
    }
    return {
        createdAtFrom: filters.createdAtFrom,
        createdAtTo: filters.createdAtTo,
        displayNames: filters.displayNames,
        machineTypeIds: filters.machineTypeIds,
        manufacturerIds: filters.manufacturerIds,
        slugs: filters.slugs,
    };
}

function machineListColumnToFitmentColumn(
    column: Exclude<MachineListServerFilterColumn, "createdAt">,
): FitmentMasterNodeListColumn {
    if (column === "type") {
        return "machineTypeId";
    }
    return column;
}

export function machineListServerFiltersKey(filters: MachineListServerFilters): string {
    const valuesKey = (values: Array<string> | undefined): string => {
        if (values === undefined) {
            return "*";
        }
        return values.join("\u0001");
    };
    return [
        valuesKey(filters.displayNames),
        valuesKey(filters.machineTypeIds),
        valuesKey(filters.manufacturerIds),
        valuesKey(filters.slugs),
        filters.createdAtFrom ?? "",
        filters.createdAtTo ?? "",
    ].join("::");
}

export type MachineType = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    displayName: string;
    slug: string;
    sortOrder: number;
    imageId: Uuid | null;
};

export type PostMachineType = {
    id?: Uuid;
    createdBy: Uuid;
    updatedBy?: Uuid;
    displayName: string;
    slug: string;
    sortOrder: number;
    imageId: Uuid | null;
};

export const machineZodValidator: ZodType<Machine> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    type: uuidZodValidator,
    manufacturerId: uuidZodValidator.nullable(),
    imageId: uuidZodValidator.nullable().optional(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
});

export const postMachineZodValidator: ZodType<PostMachine> = zod.object({
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator.optional(),
    displayName: zod.string(),
    type: uuidZodValidator,
    manufacturerId: uuidZodValidator.nullable(),
    imageId: uuidZodValidator.nullable().optional(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
});

export const listMachinesRequestZodValidator: ZodType<ListMachinesRequest> = zod.object({
    page: zod.number().optional(),
    size: zod.number().optional(),
    sort: zod.string().optional(),
    type: uuidZodValidator.optional(),
    manufacturer: uuidZodValidator.optional(),
});

export const machineTypeZodValidator: ZodType<MachineType> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
    imageId: uuidZodValidator.nullable(),
});

export const postMachineTypeZodValidator: ZodType<PostMachineType> = zod.object({
    id: uuidZodValidator.optional(),
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator.optional(),
    displayName: zod.string(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
    imageId: uuidZodValidator.nullable(),
});

function readFitmentAttributeString(attributes: Record<string, unknown> | null | undefined, key: string): string {
    if (attributes === null || attributes === undefined) {
        return "";
    }

    const value = attributes[key];
    if (typeof value !== "string") {
        return "";
    }

    return value;
}

function readFitmentAttributeUuid(attributes: Record<string, unknown> | null | undefined, key: string): Uuid | null {
    const value = readFitmentAttributeString(attributes, key);
    if (value.length === 0) {
        return null;
    }

    const parsed = uuidZodValidator.safeParse(value);
    if (parsed.success !== true) {
        return null;
    }

    return parsed.data;
}

function readFitmentAttributeNumber(attributes: Record<string, unknown> | null | undefined, key: string): number {
    if (attributes === null || attributes === undefined) {
        return 0;
    }

    const value = attributes[key];
    if (typeof value === "number" && Number.isFinite(value)) {
        return Math.max(0, Math.trunc(value));
    }

    if (typeof value === "string") {
        const parsedValue = Number(value);
        if (Number.isFinite(parsedValue)) {
            return Math.max(0, Math.trunc(parsedValue));
        }
    }

    return 0;
}

function buildMachineFitmentAttributes(machine: PostMachine | Machine): Record<string, unknown> {
    const attributes: Record<string, unknown> = {
        [MACHINE_FITMENT_ATTR_CREATED_BY]: machine.createdBy,
        [MACHINE_FITMENT_ATTR_MACHINE_TYPE_ID]: machine.type,
        [MACHINE_FITMENT_ATTR_MANUFACTURER_ID]: machine.manufacturerId,
        [MACHINE_FITMENT_ATTR_SLUG]: machine.slug,
        [MACHINE_FITMENT_ATTR_SORT_ORDER]: machine.sortOrder,
        [MACHINE_FITMENT_ATTR_UPDATED_BY]: machine.updatedBy,
    };

    if (machine.imageId !== undefined && machine.imageId !== null) {
        attributes[MACHINE_FITMENT_ATTR_IMAGE_ID] = machine.imageId;
    }

    return attributes;
}

function mapFitmentMasterNodeToMachine(node: FitmentMasterNode): Machine {
    const attributes = node.attributes ?? {};
    const createdBy =
        readFitmentAttributeUuid(attributes, MACHINE_FITMENT_ATTR_CREATED_BY) ?? node.revisionId;
    const updatedBy =
        readFitmentAttributeUuid(attributes, MACHINE_FITMENT_ATTR_UPDATED_BY) ?? node.revisionId;
    const machineTypeId =
        readFitmentAttributeUuid(attributes, MACHINE_FITMENT_ATTR_MACHINE_TYPE_ID) ?? PLACEHOLDER_MACHINE_TYPE_ID;
    const slug = readFitmentAttributeString(attributes, MACHINE_FITMENT_ATTR_SLUG);

    return {
        id: node.id,
        createdAt: node.createdAt,
        updatedAt: node.updatedAt,
        createdBy: createdBy,
        updatedBy: updatedBy,
        displayName: node.displayName,
        type: machineTypeId,
        manufacturerId: readFitmentAttributeUuid(attributes, MACHINE_FITMENT_ATTR_MANUFACTURER_ID),
        imageId: readFitmentAttributeUuid(attributes, MACHINE_FITMENT_ATTR_IMAGE_ID),
        slug: slug.length > 0 ? slug : node.displayName,
        sortOrder: readFitmentAttributeNumber(attributes, MACHINE_FITMENT_ATTR_SORT_ORDER),
    };
}

function mapMachineTypeCatalogueRowToMachineType(
    catalogueRow: TuffexProductCatalogueRow,
    columnMapping: TuffexLookupColumnMapping,
): MachineType {
    const fields = mapLookupCatalogueRowToDisplayFields({
        catalogueRow: catalogueRow,
        columnMapping: columnMapping,
    });

    return {
        id: catalogueRow.id,
        createdAt: catalogueRow.createdAt,
        updatedAt: catalogueRow.updatedAt,
        createdBy: catalogueRow.createdBy,
        updatedBy: catalogueRow.updatedBy,
        displayName: fields.displayName,
        slug: fields.slug,
        sortOrder: fields.sortOrder,
        imageId: fields.imageId,
    };
}

function mediaLibraryFileNameStem(fileName: string): string {
    const trimmed = fileName.trim().toLowerCase();
    const lastDot = trimmed.lastIndexOf(".");
    if (lastDot <= 0) {
        return trimmed;
    }
    return trimmed.slice(0, lastDot);
}

function mediaLibraryFileMatchesMachineType({
    displayName,
    fileName,
    slug,
}: {
    displayName: string;
    fileName: string;
    slug: string;
}): boolean {
    const stem = mediaLibraryFileNameStem(fileName);
    if (stem.length === 0) {
        return false;
    }

    const keys = [slug.trim().toLowerCase(), displayName.trim().toLowerCase().replace(/\s+/g, "-")].filter((key) => {
        return key.length > 0;
    });

    for (const key of keys) {
        if (stem === key || stem.startsWith(`${key}-`)) {
            return true;
        }
    }

    return false;
}

async function listMachineTypeMediaLibraryFiles(
    context: PlutoWorkspaceClientContext,
): Promise<Array<{id: Uuid; name: string}>> {
    const folderId = await findExistingMediaLibraryFolderId({
        accessToken: context.accessToken,
        folderName: MACHINE_TYPES_MEDIA_LIBRARY_FOLDER_NAME,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
    });
    if (folderId === null) {
        return [];
    }

    const dirents = await listMediaLibraryFolderFileDirents({
        accessToken: context.accessToken,
        folderId: folderId,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
    });

    return dirents.map((dirent) => {
        return {
            id: dirent.id,
            name: dirent.name,
        };
    });
}

async function attachMachineTypeImagesFromMediaLibrary({
    context,
    machineTypes,
}: {
    context: PlutoWorkspaceClientContext;
    machineTypes: Array<MachineType>;
}): Promise<Array<MachineType>> {
    const machineTypesMissingImages = machineTypes.filter((machineType) => {
        return machineType.imageId === null;
    });
    if (machineTypesMissingImages.length === 0) {
        return machineTypes;
    }

    const files = await listMachineTypeMediaLibraryFiles(context);
    if (files.length === 0) {
        return machineTypes;
    }

    return machineTypes.map((machineType) => {
        if (machineType.imageId !== null) {
            return machineType;
        }

        const matchedFile = files.find((file) => {
            return mediaLibraryFileMatchesMachineType({
                displayName: machineType.displayName,
                fileName: file.name,
                slug: machineType.slug,
            });
        });
        if (matchedFile === undefined) {
            return machineType;
        }

        return {
            ...machineType,
            imageId: matchedFile.id,
        };
    });
}

function filterMachines(machines: Array<Machine>, listParams?: ListMachinesRequest): Array<Machine> {
    let filtered = machines;

    if (listParams?.type !== undefined) {
        filtered = filtered.filter((machine) => {
            return machine.type === listParams.type;
        });
    }

    if (listParams?.manufacturer !== undefined) {
        filtered = filtered.filter((machine) => {
            return machine.manufacturerId === listParams.manufacturer;
        });
    }

    return filtered.sort((left, right) => {
        if (left.sortOrder !== right.sortOrder) {
            return left.sortOrder - right.sortOrder;
        }
        return left.displayName.localeCompare(right.displayName);
    });
}

export async function listMachines({
    filters,
    limit,
    listParams,
    offset,
    search,
    ...context
}: PlutoWorkspaceClientContext & {
    filters?: MachineListServerFilters;
    limit?: number;
    listParams?: ListMachinesRequest;
    offset?: number;
    search?: string;
}): Promise<Result<Array<Machine>>> {
    const nodesResult = await listFitmentMasterNodes({
        ...context,
        ...(limit !== undefined && offset !== undefined
            ? {
                  limit: limit,
                  offset: offset,
              }
            : {}),
        filters: machineListFiltersToFitmentFilters(filters),
        nodeType: OTTOKART_ADMIN_MACHINE_FITMENT_NODE_TYPE,
        parentId: null,
        search: search,
    });
    if (nodesResult.success !== true) {
        return nodesResult;
    }

    const machines = nodesResult.data.map((node) => {
        return mapFitmentMasterNodeToMachine(node);
    });

    return dataResult(filterMachines(machines, listParams));
}

export async function countMachines({
    filters,
    search,
    ...context
}: PlutoWorkspaceClientContext & {
    filters?: MachineListServerFilters;
    search?: string;
}): Promise<Result<number>> {
    return countFitmentMasterNodes({
        ...context,
        filters: machineListFiltersToFitmentFilters(filters),
        nodeType: OTTOKART_ADMIN_MACHINE_FITMENT_NODE_TYPE,
        parentId: null,
        search: search,
    });
}

export async function listMachineColumnValues({
    column,
    filters,
    search,
    ...context
}: PlutoWorkspaceClientContext & {
    column: Exclude<MachineListServerFilterColumn, "createdAt">;
    filters?: MachineListServerFilters;
    search?: string;
}): Promise<Result<Array<string>>> {
    return listFitmentMasterNodeColumnValues({
        ...context,
        column: machineListColumnToFitmentColumn(column),
        filters: machineListFiltersToFitmentFilters(filters),
        nodeType: OTTOKART_ADMIN_MACHINE_FITMENT_NODE_TYPE,
        parentId: null,
        search: search,
    });
}

export async function getMachineById({
    machineId,
    ...context
}: PlutoWorkspaceClientContext & {
    machineId: string;
}): Promise<Result<Machine>> {
    const nodeResult = await getFitmentMasterNodeById({
        ...context,
        id: machineId,
    });
    if (nodeResult.success !== true) {
        return nodeResult;
    }

    return dataResult(mapFitmentMasterNodeToMachine(nodeResult.data));
}

export async function createMachine({
    machine,
    ...context
}: PlutoWorkspaceClientContext & {
    machine: PostMachine;
}): Promise<Result<Uuid>> {
    const slug = machine.slug.trim();
    if (slug.length === 0) {
        return errorResult(new Error("Machine slug is required."));
    }

    return createFitmentMasterNode({
        ...context,
        attributes: buildMachineFitmentAttributes(machine),
        displayName: machine.displayName,
        nodeType: OTTOKART_ADMIN_MACHINE_FITMENT_NODE_TYPE,
        parentId: null,
    });
}

export async function updateMachine({
    machine,
    ...context
}: PlutoWorkspaceClientContext & {
    machine: Machine;
}): Promise<Result<string>> {
    const slug = machine.slug.trim();
    if (slug.length === 0) {
        return errorResult(new Error("Machine slug is required."));
    }

    const updateResult = await updateFitmentMasterNode({
        ...context,
        attributes: buildMachineFitmentAttributes(machine),
        displayName: machine.displayName,
        id: machine.id,
        nodeType: OTTOKART_ADMIN_MACHINE_FITMENT_NODE_TYPE,
        parentId: null,
    });
    if (updateResult.success !== true) {
        return updateResult;
    }

    return dataResult(machine.id);
}

export async function deleteMachineById({
    machineId,
    ...context
}: PlutoWorkspaceClientContext & {
    machineId: string;
}): Promise<Result<string>> {
    const deleteResult = await deleteFitmentMasterNode({
        ...context,
        id: machineId,
    });
    if (deleteResult.success !== true) {
        return deleteResult;
    }

    return dataResult(machineId);
}

export async function listMachineTypes(
    context: PlutoWorkspaceClientContext & {
        filters?: CatalogueTextColumnFilters;
        limit?: number;
        offset?: number;
        search?: string;
    },
): Promise<Result<Array<MachineType>>> {
    const catalogueContextResult = await loadTuffexMachineTypeCatalogue(context);
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: catalogueContextResult.data.machineTypeColumns.displayNameColumnId,
            filters: context.filters,
            slugColumnId: catalogueContextResult.data.machineTypeColumns.slugColumnId,
        }),
        productCatalogueTemplateId: catalogueContextResult.data.machineTypeColumns.templateId,
        search: context.search,
        searchColumnIds: [catalogueContextResult.data.machineTypeColumns.displayNameColumnId, catalogueContextResult.data.machineTypeColumns.slugColumnId].filter((columnId): columnId is Uuid => columnId !== null),
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const columnMapping = catalogueContextResult.data.machineTypeColumns;
    const machineTypes = rowsResult.data.map((catalogueRow) => {
        return mapMachineTypeCatalogueRowToMachineType(catalogueRow, columnMapping);
    });

    return dataResult(
        await attachMachineTypeImagesFromMediaLibrary({
            context: context,
            machineTypes: machineTypes,
        }),
    );
}

export async function countMachineTypes(context: PlutoWorkspaceClientContext & {filters?: CatalogueTextColumnFilters; search?: string}): Promise<Result<number>> {
    const catalogueContextResult = await loadTuffexMachineTypeCatalogue(context);
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }
    return countCatalogueRowsForTemplate({
        ...context,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: catalogueContextResult.data.machineTypeColumns.displayNameColumnId,
            filters: context.filters,
            slugColumnId: catalogueContextResult.data.machineTypeColumns.slugColumnId,
        }),
        productCatalogueTemplateId: catalogueContextResult.data.machineTypeColumns.templateId,
        search: context.search,
        searchColumnIds: [catalogueContextResult.data.machineTypeColumns.displayNameColumnId, catalogueContextResult.data.machineTypeColumns.slugColumnId].filter((columnId): columnId is Uuid => columnId !== null),
    });
}

export async function listMachineTypeFilterColumnValues(context: PlutoWorkspaceClientContext): Promise<Result<{displayName: Array<string>; slug: Array<string>}>> {
    const catalogueContextResult = await loadTuffexMachineTypeCatalogue(context);
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }
    const columns = catalogueContextResult.data.machineTypeColumns;
    const displayNameResult = await listCatalogueColumnValues({
        ...context,
        columnId: columns.displayNameColumnId,
        templateId: columns.templateId,
    });
    if (displayNameResult.success !== true) {
        return displayNameResult;
    }
    const slugResult = await listCatalogueColumnValues({
        ...context,
        columnId: columns.slugColumnId,
        templateId: columns.templateId,
    });
    if (slugResult.success !== true) {
        return slugResult;
    }
    return dataResult({
        displayName: displayNameResult.data,
        slug: slugResult.data,
    });
}

export async function addMachineType({
    machineType,
    ...context
}: PlutoWorkspaceClientContext & {
    machineType: PostMachineType;
}): Promise<Result<Uuid>> {
    const catalogueContextResult = await loadTuffexMachineTypeCatalogue(context);
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }

    const columnMapping = catalogueContextResult.data.machineTypeColumns;
    const slug = machineType.slug.trim();
    if (slug.length === 0) {
        return errorResult(new Error("Machine type slug is required."));
    }

    const createResult = await createCatalogueRow({
        ...context,
        additionalColumnValues: buildLookupCatalogueAdditionalColumnValues({
            columnMapping: columnMapping,
            displayName: machineType.displayName,
            imageId: machineType.imageId,
            slug: slug,
            sortOrder: machineType.sortOrder,
        }),
        parentId: null,
        productCatalogueTemplateId: columnMapping.templateId,
    });
    if (createResult.success !== true) {
        return createResult;
    }

    const createdRow = await findCatalogueRowBySlug({
        ...context,
        columnMapping: columnMapping,
        productCatalogueTemplateId: columnMapping.templateId,
        slug: slug,
    });
    if (createdRow === null) {
        return errorResult(new Error(`Created machine type catalogue row could not be found for slug: ${slug}`));
    }

    return dataResult(createdRow.id);
}

export async function updateMachineType({
    machineType,
    ...context
}: PlutoWorkspaceClientContext & {
    machineType: PostMachineType & {id: Uuid};
}): Promise<Result<string>> {
    const catalogueContextResult = await loadTuffexMachineTypeCatalogue(context);
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: catalogueContextResult.data.machineTypeColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === machineType.id;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Machine type catalogue row not found: ${machineType.id}`));
    }

    const columnMapping = catalogueContextResult.data.machineTypeColumns;
    const slug = machineType.slug.trim();
    if (slug.length === 0) {
        return errorResult(new Error("Machine type slug is required."));
    }

    const updateResult = await updateCatalogueRow({
        ...context,
        additionalColumnValues: buildLookupCatalogueAdditionalColumnValues({
            columnMapping: columnMapping,
            displayName: machineType.displayName,
            imageId: machineType.imageId,
            slug: slug,
            sortOrder: machineType.sortOrder,
        }),
        catalogueRow: catalogueRow,
        parentId: catalogueRow.parentId,
    });
    if (updateResult.success !== true) {
        return updateResult;
    }

    return dataResult(machineType.id);
}

export async function deleteMachineType({
    machineTypeId,
    ...context
}: PlutoWorkspaceClientContext & {
    machineTypeId: string;
}): Promise<Result<string>> {
    const catalogueContextResult = await loadTuffexMachineTypeCatalogue(context);
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: catalogueContextResult.data.machineTypeColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === machineTypeId;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Machine type catalogue row not found: ${machineTypeId}`));
    }

    const deleteResult = await deleteCatalogueRow({
        ...context,
        catalogueRow: catalogueRow,
    });
    if (deleteResult.success !== true) {
        return deleteResult;
    }

    return dataResult(machineTypeId);
}

export async function buildMachineByIdMap(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Map<Uuid, Machine>>> {
    const listResult = await listMachines(context);
    if (listResult.success !== true) {
        return listResult;
    }

    const machineById = new Map<Uuid, Machine>();
    for (const machine of listResult.data) {
        machineById.set(machine.id, machine);
    }

    return dataResult(machineById);
}

export async function listMachineCompatibleVariants({
    machineId,
    ...context
}: PlutoWorkspaceClientContext & {
    machineId: Uuid;
}): Promise<Result<Array<Variant>>> {
    const {listVariants} = await import("~/domainModels/ottokart/products");
    const variantsResult = await listVariants(context);
    if (variantsResult.success !== true) {
        return variantsResult;
    }

    return dataResult(
        variantsResult.data.filter((variant) => {
            return variant.compatibleMachines.some((machine) => {
                return machine.id === machineId;
            });
        }),
    );
}
